import { getApiUrl } from "../utils/envConfig";
import { makeAuthenticatedRequest } from "./marketOverviewApi";

/**
 * Fetch the canonical platform name + color mapping from the API.
 *
 * Response shape:
 * {
 *   version: string,                          // 12-char hash; use for cache invalidation
 *   entries: Array<{platform, id_platform, country}>,
 *   display_name_overrides: Record<string, string>,
 *   colors: Record<string, string>,
 * }
 *
 * @returns {Promise<object>}
 */
export const fetchPlatformMetadata = async (): Promise<
  Record<string, unknown>
> => {
  const url = `${getApiUrl()}/platforms/metadata`;
  return makeAuthenticatedRequest(url);
};

// localStorage cache for `/platforms/metadata`. The data changes rarely
// (a new platform getting onboarded) but the failure mode of a single
// botched fetch is severe — `platformMapping.js` ships with no
// hardcoded seed (clients on bad internet shouldn't see platforms they
// don't have), so without a cache hit the UI would render raw
// `id_platform` codes (TKW, WOL) instead of names (Lieferando, Wolt)
// for the rest of the session. Caching the last good response lets a
// transient fetch failure recover gracefully.
//
// Cache key includes the API base URL so switching between dev / stg /
// prod sessions on the same machine doesn't mix entries.
const CACHE_VERSION = "v1";

const cacheKey = () => `platform_metadata_cache:${CACHE_VERSION}:${getApiUrl()}`;

/**
 * Read the cached metadata payload, or `null` when nothing is cached
 * or the cache is unreadable (private mode / disabled localStorage).
 *
 * @returns {object|null} the cached payload, or null
 */
export const readPlatformMetadataCache = (): Record<
  string,
  unknown
> | null => {
  try {
    const raw = localStorage.getItem(cacheKey());
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.entries) || parsed.entries.length === 0) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

/**
 * Persist a fresh metadata payload to localStorage. No-op on quota /
 * private-mode failures — the in-memory state is already updated by
 * `applyPlatformMetadata`, so a write miss is non-fatal.
 *
 * @param {object} payload
 */
export const writePlatformMetadataCache = (
  payload: Record<string, unknown>
) => {
  if (!payload || !Array.isArray(payload.entries) || payload.entries.length === 0) {
    return;
  }
  try {
    localStorage.setItem(cacheKey(), JSON.stringify(payload));
  } catch {
    // Quota / disabled storage — ignore. Live response stays in memory.
  }
};
